import { useState, useRef, useCallback } from 'react';
import { updateSpreadsheetCells, autoResizeTextarea } from '../utils/helpers';

export const useSpreadsheetEditing = ({
  data,
  filteredData,
  getVisibleColumns,
  visibleColumnNames,
  currentSheetType,
  year,
  updateCells,
  activeCell,
  setActiveCell,
  setSelectionStart,
  setSelectionEnd,
  scrollToCell,
  editingCell,
  setEditingCell,
  editingValue,
  setEditingValue,
  replaceOnNextEdit,
  cursorPositionRef,
}) => {

  const enterEditMode = useCallback((rowIndex, colIndex, initialKey = '', replace = false) => {
    if (!filteredData[rowIndex]) return;

    const columnKey = Object.keys(getVisibleColumns())[colIndex];
    const rowIndexInData = filteredData[rowIndex]._originalIndex;
    const currentCellValue = data[rowIndexInData][columnKey];

    setEditingCell([rowIndex, colIndex]);
    setActiveCell([rowIndex, colIndex]);
    setSelectionStart([rowIndex, colIndex]);
    setSelectionEnd([rowIndex, colIndex]);

    replaceOnNextEdit.current = replace;

    if (replace) {
      setEditingValue(initialKey);
    } else {
      setEditingValue(currentCellValue || '');
    }
  }, [
    filteredData,
    data,
    getVisibleColumns,
    setEditingCell,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
  ]);

  const handleEditingChange = useCallback((e) => {
    const newValue = e.target.value;
    cursorPositionRef.current = e.target.selectionStart;
    
    setEditingValue(newValue);
    // Only auto-resize if it's an actual DOM element (not from formula bar)
    if (e.target && e.target.style) {
      autoResizeTextarea(e.target);
    }
  }, [setEditingValue]);

  const finishEditing = useCallback(async (cancel = false) => {
    if (editingCell) {
      try {
        const [rowIndex, colIndex] = editingCell;
        const rowData = filteredData[rowIndex];
        const columnKey = Object.keys(getVisibleColumns())[colIndex];
        const oldValue = rowData[columnKey];
        const newValue = editingValue;

        if (!cancel && oldValue !== newValue) {
          // Use the React Query mutation
          updateCells([{
            unid: rowData.unid,
            columnId: columnKey,
            value: newValue
          }]);
        }
      } catch (error) {
        console.error('Error updating cell value:', error);
      }
      setEditingCell(null);
      setEditingValue('');
    }
  }, [
    editingCell,
    editingValue,
    data,
    filteredData,
    getVisibleColumns,
    currentSheetType,
    year,
    updateCells,
  ]);

  const handleEditingKeyDown = useCallback((e) => {
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
      e.preventDefault();
      finishEditing();
      const [row, col] = activeCell;
      
      setSelectionStart([row + 1, col]);
      setSelectionEnd([row + 1, col]);
      setActiveCell([row + 1, col]);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      if (editingCell) {
        finishEditing();
      }

      if (activeCell) {
        const [row, col] = activeCell;
        let newRow = row;
        let newCol = col;

        if (e.shiftKey) {
          if (col > 0) {
            newCol = col - 1;
          } else if (row > 0) {
            newRow = row - 1;
            newCol = Object.keys(visibleColumnNames).length - 1;
          }
        } else {
          if (col < Object.keys(visibleColumnNames).length - 1) {
            newCol = col + 1;
          } else if (row < filteredData.length - 1) {
            newRow = row + 1;
            newCol = 0;
          }
        }

        setActiveCell([newRow, newCol]);
        setSelectionStart([newRow, newCol]);
        setSelectionEnd([newRow, newCol]);
        
        setTimeout(() => {
          scrollToCell(newRow, newCol);
        }, 0);
      }
    } else if (e.key === 'Escape') {
      if (editingCell) {
        const [row, col] = editingCell;
        const rowIndexInData = filteredData[row]._originalIndex;
        const columnKey = Object.keys(visibleColumnNames)[col];
        const originalValue = data[rowIndexInData][columnKey];
        setEditingValue(originalValue);
      }
      setEditingCell(null);
      setEditingValue('');
    }
  }, [
    editingCell,
    finishEditing,
    filteredData,
    visibleColumnNames,
    data,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
    scrollToCell,
  ]);

  return {
    enterEditMode,
    handleEditingChange,
    finishEditing,
    handleEditingKeyDown,
  };
};